// A plan's summary, status, progress and claim (today's doc/PlanHeader.tsx).
// Each change is the store's, through its outbox: shown at once, rolled back
// with a word when the server refuses it. Labels on plans are left out.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Actor, PlanRow, PlanStatus } from '../api/types'
import { PlanStatusIcon } from '../components/Icons'
import { InlineText } from '../components/InlineText'
import { Time } from '../components/Time'
import { projectPath } from '../projects/routing'
import { senderName } from '../sessions/words'
import { useBoardState, useStore } from '../store/context'
import { PLAN_STATUSES } from '../vocab'
import { ProgressBar } from './ProgressBar'
import { planProgress } from './state'
import { pageWords as w } from './words'

export function PlanHeader({ plan, projectKey }: { plan: PlanRow; projectKey: string }) {
  const store = useStore()
  const { issues, me } = useBoardState()
  const [changingClaim, setChangingClaim] = useState(false)
  const progress = planProgress(plan, issues)
  const mine = plan.claim ? sameHolder(plan.claim.by, me?.actor) : false

  const changeClaim = async () => {
    if (changingClaim || !me) return
    setChangingClaim(true)
    try {
      await (plan.claim ? store.releasePlan(plan.id) : store.claimPlan(plan.id))
    } finally {
      setChangingClaim(false)
    }
  }

  return (
    <div className="plan-head">
      <InlineText
        as="p"
        className="issue-summary"
        value={plan.summary}
        onSave={(summary) => void store.updatePlan(plan.id, { summary })}
        placeholder={w.summaryPlaceholder}
        name="summary"
        maxLength={255}
      />
      <dl className="props props-inline">
        <div className="prop">
          <dt>{w.status}</dt>
          <dd className="prop-control">
            <PlanStatusIcon status={plan.status} />
            <select aria-label={w.planStatus} value={plan.status} onChange={(e) => void store.updatePlan(plan.id, { status: e.target.value as PlanStatus })}>
              {PLAN_STATUSES.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </dd>
        </div>
        <div className="prop">
          <dt>{w.progress}</dt>
          <dd>
            <ProgressBar progress={progress} label={w.listedIssues} />{' '}
            <Link to={`${projectPath(projectKey, '/board')}?plan=${encodeURIComponent(plan.id)}`} className="button button-quiet button-small">
              {w.showOnBoard}
            </Link>
          </dd>
        </div>
        <div className="prop">
          <dt>{w.claim}</dt>
          <dd className="prop-claim">
            {plan.claim ? (
              <span>
                {senderName(plan.claim.by, me)} · <Time iso={plan.claim.at} />
              </span>
            ) : (
              <span className="muted">{w.unclaimed}</span>
            )}
            <button type="button" className="button button-small" disabled={!me || changingClaim} onClick={() => void changeClaim()}>
              {!plan.claim ? w.claimAction : mine ? w.unclaimAction : w.releaseClaim}
            </button>
          </dd>
        </div>
      </dl>
    </div>
  )
}

function sameHolder(holder: Actor, actor: Actor | undefined): boolean {
  if (!actor || holder.kind !== actor.kind) return false
  if (holder.kind === 'person' && actor.kind === 'person') return holder.member === actor.member
  if (holder.kind === 'agent' && actor.kind === 'agent') return holder.session === actor.session
  return holder.kind === 'poulpe'
}
